import { AdminTable } from './shared/AdminDataGrid';
/**
 * Card Edit Component
 * Form for editing existing profile cards with translation management
 */

import { useState } from 'react';
import { CardScoringInputs } from './CardScoringInputs';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  required,
  useRecordContext,
  useRefresh,
  useNotify,
} from 'react-admin';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TranslateIcon from '@mui/icons-material/Translate';
import { CARD_TYPE_CHOICES, CARD_STATUS_CHOICES, SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from '../constants';
import { adminApi } from '../providers/dataProvider';

interface CardTranslation {
  language: string;
  statement: string;
}

interface CardRecord {
  id: string;
  statement: string;
  type: string;
  categoryId: string;
  categoryName: string | null;
  subdomainId: string;
  status: string;
  translations?: CardTranslation[];
}

/**
 * Title for the edit page
 */
function CardTitle() {
  const record = useRecordContext<CardRecord>();
  return <span>Edit Card {record ? `"${record.statement?.substring(0, 30)}..."` : ''}</span>;
}

/**
 * Translation management section
 */
function TranslationsSection() {
  const record = useRecordContext<CardRecord>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState<CardTranslation | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [translatedStatement, setTranslatedStatement] = useState('');
  const [loading, setLoading] = useState(false);

  if (!record) return null;

  const existingLanguages = record.translations?.map(t => t.language) || [];
  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    lang => lang.id !== 'en' && !existingLanguages.includes(lang.id)
  );

  const handleOpenDialog = (translation?: CardTranslation) => {
    if (translation) {
      setEditingTranslation(translation);
      setSelectedLanguage(translation.language);
      setTranslatedStatement(translation.statement);
    } else {
      setEditingTranslation(null);
      setSelectedLanguage('');
      setTranslatedStatement('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTranslation(null);
    setSelectedLanguage('');
    setTranslatedStatement('');
  };

  const handleSaveTranslation = async () => {
    if (!selectedLanguage || !translatedStatement.trim()) {
      notify('Please fill in all fields', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await adminApi.upsertCardTranslation(record.id, selectedLanguage, translatedStatement);
      notify(editingTranslation ? 'Translation updated' : 'Translation added', { type: 'success' });
      refresh();
      handleCloseDialog();
    } catch (error) {
      notify('Failed to save translation', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (translation: CardTranslation) => {
    setEditingTranslation(translation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingTranslation) return;

    setLoading(true);
    try {
      await adminApi.deleteCardTranslation(record.id, editingTranslation.language);
      notify('Translation deleted', { type: 'success' });
      refresh();
      setDeleteDialogOpen(false);
      setEditingTranslation(null);
    } catch (error) {
      notify('Failed to delete translation', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TranslateIcon />
            <Typography variant="h6">
              Translations ({record.translations?.length || 0})
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={availableLanguages.length === 0}
            size="small"
          >
            Add Translation
          </Button>
        </Box>

        {record.translations && record.translations.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <AdminTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={120}>Language</TableCell>
                  <TableCell>Translated Statement</TableCell>
                  <TableCell width={100} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.translations.map((translation) => (
                  <TableRow key={translation.language}>
                    <TableCell>
                      <Chip
                        label={LANGUAGE_LABELS[translation.language] || translation.language}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{translation.statement}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(translation)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(translation)}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </AdminTable>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No translations available. Click "Add Translation" to add one.
          </Alert>
        )}

        {/* Add/Edit Translation Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTranslation ? `Edit ${LANGUAGE_LABELS[editingTranslation.language]} Translation` : 'Add Translation'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {!editingTranslation && (
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={selectedLanguage}
                    label="Language"
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                  >
                    {availableLanguages.map((lang) => (
                      <MenuItem key={lang.id} value={lang.id}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="textSecondary">Original (English)</Typography>
                <Typography variant="body2">{record.statement}</Typography>
              </Box>

              <MuiTextField
                label="Translated Statement"
                value={translatedStatement}
                onChange={(e) => setTranslatedStatement(e.target.value)}
                multiline
                rows={3}
                fullWidth
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveTranslation}
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Translation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Translation</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the {editingTranslation ? LANGUAGE_LABELS[editingTranslation.language] : ''} translation?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function CardEdit() {
  return (
    <Edit title={<CardTitle />} mutationMode="pessimistic" transform={({ statement, categoryId, subdomainId, status, scoringDirection, reviewScoring }) =>
      ({ statement, categoryId, subdomainId, status, scoringDirection, reviewScoring })}>
      <Box sx={{ p: 2 }}>
        <SimpleForm>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Typography variant="h6" gutterBottom>Card Details</Typography>
            <CardScoringInputs />

            <TextInput
              source="statement"
              label="Statement"
              multiline
              rows={3}
              validate={required()}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <SelectInput
                source="type"
                label="Type"
                choices={CARD_TYPE_CHOICES}
                disabled
                sx={{ minWidth: 200 }}
              />

              <ReferenceInput
                source="categoryId"
                reference="content/categories"
                label="Category"
              >
                <SelectInput
                  optionText="name"
                  optionValue="id"
                  validate={required()}
                  sx={{ minWidth: 200 }}
                />
              </ReferenceInput>

              <SelectInput
                source="status"
                label="Status"
                choices={CARD_STATUS_CHOICES}
                validate={required()}
                sx={{ minWidth: 200 }}
              />
            </Box>

            <TextInput
              source="subdomainId"
              label="Subdomain ID"
              validate={required()}
            />
          </Box>
        </SimpleForm>

        <Divider sx={{ my: 2 }} />

        <TranslationsSection />
      </Box>
    </Edit>
  );
}
